import { useMemo, useState, type ComponentType, type HTMLAttributes } from 'react';
import { TickCircle, Messages2, MoreVertical, ArrowRotateLeft, Trash } from '@/components/icons';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { ReportComment } from '@/types/report-comments';
import {
    formatCommentTimestamp,
    getCommentAuthorLabel,
    getNameInitials,
    sortCommentMessagesByCreatedAt,
} from '../utils';
import { CommentComposer } from './CommentComposer';

interface CommentThreadProps {
    comment: ReportComment;
    sectionLabel?: string;
    currentUserId?: string | null;
    currentUserName?: string | null;
    isReplyPending?: boolean;
    isStatusPending?: boolean;
    isDeletePending?: boolean;
    onReply: (commentId: string, content: string) => Promise<void>;
    onToggleStatus: (commentId: string, nextStatus: 'active' | 'completed') => Promise<void>;
    onDelete: (commentId: string) => Promise<void>;
}

interface CommentMessageItemProps {
    message: ReportComment['messages'][number];
    currentUserId?: string | null;
    currentUserName?: string | null;
    variant?: 'root' | 'reply';
}

function CommentMessageItem({
    message,
    currentUserId,
    currentUserName,
    variant = 'root',
}: CommentMessageItemProps) {
    const TypedAvatar = Avatar as ComponentType<HTMLAttributes<HTMLDivElement>>;
    const TypedAvatarFallback = AvatarFallback as ComponentType<HTMLAttributes<HTMLDivElement>>;

    const authorLabel = getCommentAuthorLabel({
        authorId: message.author_id,
        currentUserId,
        currentUserName,
    });
    const isReply = variant === 'reply';

    return (
        <div className="flex items-start gap-3">
            <TypedAvatar className={cn('border border-border/80', isReply ? 'size-8' : 'size-9')}>
                <TypedAvatarFallback
                    className={cn(
                        'bg-surface-muted font-semibold text-muted-foreground',
                        isReply ? 'text-[11px]' : 'text-xs',
                    )}
                >
                    {getNameInitials(authorLabel)}
                </TypedAvatarFallback>
            </TypedAvatar>

            <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{authorLabel}</span>
                    <span>{formatCommentTimestamp(message.created_at)}</span>
                    {message.edited_at && <span>(editado)</span>}
                </div>

                <p className="whitespace-pre-wrap break-words text-sm leading-6 text-foreground">
                    {message.content}
                </p>
            </div>
        </div>
    );
}

export function CommentThread({
    comment,
    sectionLabel,
    currentUserId,
    currentUserName,
    isReplyPending = false,
    isStatusPending = false,
    isDeletePending = false,
    onReply,
    onToggleStatus,
    onDelete,
}: CommentThreadProps) {
    const [isReplying, setIsReplying] = useState(false);

    const sortedMessages = useMemo(
        () => sortCommentMessagesByCreatedAt(comment.messages || []),
        [comment.messages],
    );
    const rootMessage = sortedMessages[0];
    const replies = sortedMessages.slice(1);
    const messageCount = sortedMessages.length;

    const handleReply = async (content: string) => {
        await onReply(comment.id, content);
        setIsReplying(false);
    };

    const nextStatus = comment.status === 'active' ? 'completed' : 'active';

    return (
        <Card className="border-border/80 bg-card shadow-elevation-2">
            <CardContent className="p-4">
                <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            {sectionLabel && <Badge variant="neutral">{sectionLabel}</Badge>}
                            <Badge variant={comment.status === 'active' ? 'info' : 'success'}>
                                {comment.status === 'active' ? 'Aberto' : 'Resolvido'}
                            </Badge>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={isStatusPending}
                            onClick={() => onToggleStatus(comment.id, nextStatus)}
                            className="h-8 px-2"
                        >
                            {comment.status === 'active' ? (
                                <TickCircle className="size-4" />
                            ) : (
                                <ArrowRotateLeft className="size-4" />
                            )}
                            {comment.status === 'active' ? 'Resolver' : 'Reabrir'}
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-muted-foreground"
                                >
                                    <MoreVertical className="size-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    disabled={isDeletePending}
                                    onClick={() => onDelete(comment.id)}
                                    className="cursor-pointer text-destructive focus:text-destructive"
                                >
                                    <Trash className="size-4" />
                                    Excluir comentário
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                <div className="mt-4 space-y-4">
                    {rootMessage && (
                        <CommentMessageItem
                            message={rootMessage}
                            currentUserId={currentUserId}
                            currentUserName={currentUserName}
                        />
                    )}

                    {replies.length > 0 && (
                        <>
                            <Separator />
                            <div className="space-y-3">
                                <p className="text-xs font-medium tracking-wide text-muted-foreground">
                                    {replies.length}{' '}
                                    {replies.length === 1 ? 'complemento' : 'complementos'}
                                </p>
                                <div className="ml-4 rounded-xl border border-border/60 bg-surface/70 p-4">
                                    <div className="space-y-4">
                                        {replies.map((message, index) => (
                                            <div key={message.id} className="space-y-4">
                                                {index > 0 && <Separator />}
                                                <CommentMessageItem
                                                    message={message}
                                                    currentUserId={currentUserId}
                                                    currentUserName={currentUserName}
                                                    variant="reply"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {isReplying ? (
                    <div className="mt-4 rounded-xl border border-border/70 bg-surface/60 p-3">
                        <CommentComposer
                            placeholder="Adicione uma informação complementar..."
                            submitLabel="Adicionar complemento"
                            isSubmitting={isReplyPending}
                            autoFocus
                            onSubmit={handleReply}
                            onCancel={() => setIsReplying(false)}
                        />
                    </div>
                ) : (
                    <div className="mt-4 flex items-center justify-between gap-3 border-t border-border/70 pt-3">
                        <p className="text-xs text-muted-foreground">
                            {messageCount} {messageCount === 1 ? 'mensagem' : 'mensagens'}
                        </p>

                        <Button
                            type="button"
                            variant="surface"
                            size="sm"
                            onClick={() => setIsReplying(true)}
                        >
                            <Messages2 className="size-4" />
                            Complementar
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
